/**
 * @typedef {object} FirebaseEvent Firebase Analyticsに送信するイベント情報
 * @property {string} eventName - Firebaseに送信するイベント名 (例: 'mng_user_action')
 * @property {string} actionType - イベントのアクション種別を示すパラメータ (例: 'view_app_deeplink_webview')
 * @property {string} actionValue - イベントのアクション内容を示すパラメータ (例: 'プレミアム案内/プレミアムサービスについて')
 */

/**
 * @typedef {object} ActionConfig ユーザーアクションに応じて実行される一連の動作を定義するオブジェクト
 * @description
 * Webビューなどからネイティブアプリの各種機能を呼び出す際に使用します。
 * 画面遷移（外部リンク、ディープリンク、閉じる）とイベントトラッキング（Firebase, Repro）を制御できます。
 *
 * **【重要】**
 * 画面遷移アクション (`openOuterUrl`, `openInnerDeepLink`, `closePage`) は排他的です。
 * これらは同時に複数指定することはできず、いずれか1つのみを指定してください。
 *
 * @property {string} [openOuterUrl] - 指定されたURLを、アプリの外部ブラウザで開きます。
 * @property {string} [openInnerDeepLink] - アプリ内の特定画面へ遷移するためのディープリンクパラメータです (例: 'dt', 'wt')。
 * @property {boolean} [closePage] - `true` の場合、現在表示しているWebビューや画面を閉じます。
 * @property {FirebaseEvent} [firebase] - Firebase Analyticsに送信するイベント情報を定義します。
 * @property {string} [repro] - Reproに送信するカスタムイベント名を指定します (例: 'screen_xxx')。
 */

// ★★★ ここから下を追記 ★★★

/**
 * @typedef {object} NativeMessage ネイティブアプリから受信するメッセージの基本形
 * @description
 * ネイティブアプリからWebViewに送信されるメッセージのデータ構造を定義します。
 * `type` プロパティでメッセージの種類を識別し、`payload` に具体的なデータを含めることを想定しています。
 *
 * @property {string} type - メッセージの種類を識別する文字列 (例: 'UPDATE_USER_INFO', 'AUTH_TOKEN_EXPIRED')
 * @property {object} [payload] - メッセージに関連するデータを含むオブジェクト
 */

/**
 * ネイティブアプリからのメッセージを受信し、指定されたコールバック関数を実行します。
 *
 * @param {(data: NativeMessage) => void} callback - メッセージ受信時に実行される関数。パースされたデータが引数として渡されます。
 * @returns {() => void} イベントリスナーを解除するためのクリーンアップ関数を返します。
 *                        不要になった際に呼び出して、メモリリークを防ぎます。
 */
export function listenMessageFromNative(callback) {
  const handleMessage = (event) => {
    // ... (セキュリティチェックなどは変更なし) ...

    let data;

    if (typeof event.data === 'string') {
      try {
        data = JSON.parse(event.data);
      } catch (e) {
        console.error("🚨 Failed to parse message data as JSON:", event.data, e);
        return;
      }
    } else {
      data = event.data;
    }

    if (typeof data !== 'object' || data === null) {
      console.warn("🎨 Received message is not a valid object:", data);
      return;
    }
    // --- ▲▲▲ ここまで修正 ▲▲▲ ---

    console.groupEnd();

    callback(data);
  };

  window.addEventListener('message', handleMessage);

  const cleanup = () => {
    window.removeEventListener('message', handleMessage);
    console.log("✅ Message listener has been cleaned up.");
  };

  return cleanup;
}


export function sendMessageToNative(data) {
  // 送信するメッセージを、コンソールで読みやすいように整形されたJSON文字列に変換
  const message = JSON.stringify(data, null, 2);

  // WebView内で実行されているかどうかを判定
  if (window.parent && window.parent !== window) {
    // --- [本番] WebView内で実行されている場合の処理 ---

    // コンソールに送信内容をグループ化して表示
    console.group("🚀 postMessage Sent to Native App");
    console.log("Payload (Object):", data);
    console.log("Sent as (JSON String):", message);
    console.groupEnd();

    // ネイティブアプリにメッセージを送信
    const targetOrigin = '*';
    window.parent.postMessage(message, targetOrigin);

  } else {
    // --- [開発用] PCブラウザなど、WebView外で実行されている場合のフォールバック処理 ---
    // コンソールに「シミュレーションであること」と送信内容をグループ化して表示
    console.group("🎨 postMessage (Simulated for Browser)");
    console.warn("Not running in a WebView. The message was not actually sent to a parent window.");
    console.log("Payload (Object):", data);
    console.log("Would be sent as (JSON String):", message);
    console.groupEnd();
  }
}
